import React, { useState, SyntheticEvent, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

// MUI
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Checkbox from '@mui/material/Checkbox';
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useTheme, useMediaQuery } from "@mui/material";

// Custom Components
import DynamicDialog from "@/ui/Dialog/DynamicDialog";
import DialogBody from "@/ui/Dialog/DialogBody";
import LanguageSelector from "@/ui/Header/LanguageSelectorPublic";
import WelcomeModelCard from "@/views/HomePage/WelcomeModelCard";
import WelcomeModelCardDivider from "@/views/HomePage/WelcomeModelCardDivider";

// Icons
import ToolSearchIcon from "@/icons/ToolSearchIcon";
import ToolRouteIcon from "@/icons/ToolRouteIcon";
import ToolSettingsIcon from "@/icons/ToolSettingsIcon";
import ToolCommentIcon from "@/icons/ToolCommentIcon";

// Types
import { IDialogProps } from "@/@types/ui/DialogContext";
import LogoIcon from "@/icons/LogoIcon";

export type WelcomeModalDialogProps = {
  onClose: () => void;
}

const WelcomeModalDialog: FunctionComponent<IDialogProps> = (props) => {
  const { t } = useTranslation();

  const theme = useTheme();

  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const [dontShow, setDontShow] = useState(false);

  const { onClose } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDontShow(event.target.checked);
  };

  const handleClose = (evt: SyntheticEvent) => {
    if (dontShow) {
      localStorage.setItem('welcome_dont_show', 'true');
    }
    onClose();
  };

  return (
    <DynamicDialog open maxWidth="lg" onClose={handleClose}>
      <div style={{ width: "100%", height: "100%" }} className="welcome-dialog-wrapper" >
        <DialogBody>
          <Box width="100%" height="50px" display="flex" justifyContent="flex-end">
            <LanguageSelector />
          </Box>
          <Box ml={3} mt={1} display="flex" justifyContent="space-between" alignItems="flex-start" sx={{
            flexDirection: {
              xs: "column", md: "row"
            }
          }}>
            <Box sx={{
              maxWidth: "600px",
              display: "inline-block"
            }}>

              <Typography color={theme.palette.primary.main} variant="h4" paragraph sx={{
                fontSize: {
                  md: "1.9rem",
                  xs: "1.6rem"
                }
              }}>
                {t("welcome.intro1")} <LogoIcon fontSize="large" sx={{ verticalAlign: "top" }} /> {t("welcome.intro2")}
              </Typography>
            </Box>
            <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" mr="20px" sx={{
              maxWidth: "400px"
            }}>
              <img width="100%" src="images/logos_welcome.png" alt="logos: Ministry, HPS, HGSS" />
            </Box>
          </Box>
          <Grid container columns={12} color={theme.palette.primary.main} mt={mdUp ? 13 : 8} spacing={0}>
            <Grid size={{ xs: 12, md: 3 }}>
              <WelcomeModelCard
                icon={<ToolSearchIcon fontSize="large" transform="scale(1.75)" />}
                title={t("welcome.search_title")}
                description={t("welcome.search_content")}
              />
            </Grid>
            <WelcomeModelCardDivider />
            <Grid size={{ xs: 12, md: 3 }}>
              <WelcomeModelCard
                icon={<ToolRouteIcon fontSize="large" transform="scale(1.75)" />}
                title={t("welcome.route_title")}
                description={t("welcome.route_content")}
              />
            </Grid>
            <WelcomeModelCardDivider />
            <Grid size={{ xs: 12, md: 3 }}>
              <WelcomeModelCard
                icon={<ToolSettingsIcon fontSize="large" transform="scale(1.75)" />}
                title={t("welcome.layers_title")}
                description={t("welcome.layers_content")}
              />
            </Grid>
            <WelcomeModelCardDivider />
            <Grid size={{ xs: 12, md: 3 }}>
              <WelcomeModelCard
                icon={<ToolCommentIcon fontSize="large" transform="scale(1.75)" />}
                title={t("welcome.comments_title")}
                description={t("welcome.comments_content")}
              />
            </Grid>
          </Grid>
        </DialogBody>
        <DialogActions sx={{ mt: { xs: 5, md: 2 } }}>
          <FormControlLabel
            value="top"
            control={<Checkbox
              checked={dontShow}
              onChange={handleChange}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 28 }, mr: 2 }}
            />}
            label={t("welcome.dont_show")}
            labelPlacement="start"

          />
          <Box width="175px">
            <Button
              fullWidth
              color="primary"
              size="medium"
              onClick={handleClose}
              variant="contained"
              aria-label="close"
              aria-controls="close"
            >
              {t("buttons.close")}
            </Button>
          </Box>
        </DialogActions>
      </div>
    </DynamicDialog>
  );
};

export default WelcomeModalDialog;
